/**
 * Cutaway actions: the cutaway changes scene elements make when activated (hotspot / model /
 * menu-item clicks, tour stop and extra waypoint arrivals; see CutawayAction in ../types). Pure
 * logic, no engine import, so the editor can share it: createViewer runs the plan and steps the
 * transition each frame; the plane itself is effects/cutaway.ts.
 */
import type { CutawayAction } from '../types';
import type { CutawayDirection } from './cutaway';

/** Where an action with no `level` puts the plane: half way up. */
export const CUTAWAY_ACTION_DEFAULT_LEVEL = 0.5;
/** How long an action with no `duration` slides the plane, in seconds. */
export const CUTAWAY_ACTION_DEFAULT_DURATION = 1;
/** Longest slide an action may ask for, in seconds (hand-edited JSON). */
const CUTAWAY_ACTION_MAX_DURATION = 30;

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** The level at which a side hides nothing: the plane at the top when hiding above, at the bottom when hiding below. */
export function cutawayClearLevel(direction: CutawayDirection): number {
    return direction === 'below' ? 0 : 1;
}

/** A cutaway action with its duration filled in (and its level, when it turns the cutaway on). */
export type NormalizedCutawayAction = CutawayAction & { duration: number };

/**
 * A well-formed copy of an element's `cutaway` field, defaults filled in, or null when it has
 * none (or it is malformed: no boolean `enabled`).
 */
export function normalizeCutawayAction(input: unknown): NormalizedCutawayAction | null {
    if (!input || typeof input !== 'object') return null;
    const raw = input as Record<string, unknown>;
    if (typeof raw.enabled !== 'boolean') return null;
    const duration = finite(raw.duration) && raw.duration >= 0 ? Math.min(raw.duration, CUTAWAY_ACTION_MAX_DURATION) : CUTAWAY_ACTION_DEFAULT_DURATION;
    const action: NormalizedCutawayAction = { enabled: raw.enabled, duration };
    if (raw.enabled) action.level = clamp01(finite(raw.level) ? raw.level : CUTAWAY_ACTION_DEFAULT_LEVEL);
    if (raw.direction === 'above' || raw.direction === 'below') action.direction = raw.direction;
    return action;
}

/** The plane sliding from one level to another (see stepCutawayTransition). */
export interface CutawayTransition {
    from: number;
    to: number;
    /** Seconds; 0 lands on `to` at the first step. */
    duration: number;
    /** Seconds run so far. */
    elapsed: number;
    /** Turn the cutaway off once the plane arrives (an `enabled: false` action). */
    disableAtEnd: boolean;
}

/** What running an action changes, from the cutaway's current state. */
export interface CutawayPlan {
    /** The side to hide from now on. */
    direction: CutawayDirection;
    /** Turn the cutaway on before the plane moves (it was off). */
    enable: boolean;
    transition: CutawayTransition;
}

export interface CutawayCurrentState {
    active: boolean;
    level: number;
    direction: CutawayDirection;
}

/**
 * How the cutaway gets from `current` to what `action` asks for, or null when there is nothing
 * to do (turning off a cutaway that is already off).
 * - Turning on starts the plane where it hides nothing, so the slice sweeps in.
 * - Already on, the plane slides on from where it is, even mid-slide, so a new trigger retargets
 *   smoothly. Changing the hidden side starts over from that side's clear end.
 * - Turning off slides the plane out of the way, then switches the cutaway off.
 */
export function planCutawayAction(current: CutawayCurrentState, input: CutawayAction): CutawayPlan | null {
    const action = normalizeCutawayAction(input);
    if (!action) return null;
    if (!action.enabled) {
        if (!current.active) return null;
        return {
            direction: current.direction,
            enable: false,
            transition: { from: current.level, to: cutawayClearLevel(current.direction), duration: action.duration, elapsed: 0, disableAtEnd: true },
        };
    }
    const direction = action.direction ?? current.direction;
    const from = current.active && direction === current.direction ? clamp01(current.level) : cutawayClearLevel(direction);
    return {
        direction,
        enable: !current.active,
        transition: { from, to: action.level ?? CUTAWAY_ACTION_DEFAULT_LEVEL, duration: action.duration, elapsed: 0, disableAtEnd: false },
    };
}

/** Ease in and out (cubic), like the tour's default easing. */
function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Advance `transition` by `dt` seconds (it keeps the time run): the plane level now, and whether it has arrived. */
export function stepCutawayTransition(transition: CutawayTransition, dt: number): { level: number; done: boolean } {
    transition.elapsed += finite(dt) && dt > 0 ? dt : 0;
    const t = transition.duration > 0 ? Math.min(1, transition.elapsed / transition.duration) : 1;
    if (t >= 1) return { level: transition.to, done: true };
    return { level: transition.from + (transition.to - transition.from) * easeInOutCubic(t), done: false };
}

/** The scene data a cutaway action can live on (loosely typed: scene JSON as loaded). */
export interface CutawayActionSources {
    hotspots?: ReadonlyArray<{ cutaway?: unknown } | null | undefined>;
    waypoints?: ReadonlyArray<{ cutaway?: unknown } | null | undefined>;
    extraWaypoints?: ReadonlyArray<{ cutaway?: unknown } | null | undefined>;
    customMeshes?: ReadonlyArray<{ interaction?: { cutaway?: unknown } | null } | null | undefined>;
    uiOptions?: { sceneMenuLinks?: ReadonlyArray<{ cutaway?: unknown } | null | undefined> } | null;
}

/** True when any hotspot, tour stop, extra waypoint, model or menu item carries a cutaway action. */
export function sceneHasCutawayActions(scene: CutawayActionSources): boolean {
    const any = (list: ReadonlyArray<{ cutaway?: unknown } | null | undefined> | undefined) =>
        Array.isArray(list) && list.some((item) => normalizeCutawayAction(item?.cutaway) !== null);
    return any(scene.hotspots) || any(scene.waypoints) || any(scene.extraWaypoints) || any(scene.uiOptions?.sceneMenuLinks)
        || (Array.isArray(scene.customMeshes) && scene.customMeshes.some((m) => normalizeCutawayAction(m?.interaction?.cutaway) !== null));
}
